/**
 * MarcasPage Component
 * 
 * Página de listagem e gerenciamento de marcas de produtos.
 * Permite visualizar, criar, editar e excluir marcas.
 * 
 * Funcionalidades:
 * - Listagem de marcas com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Exclusão com confirmação
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search, Tag } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { brandService } from '../../../../services/erpApi'
import './MarcasPage.css'

function MarcasPage() {
  const [marcas, setMarcas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingMarca, setEditingMarca] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    active: true
  })
  const [errors, setErrors] = useState({})
  const [searchTerm, setSearchTerm] = useState('')

  const loadMarcas = useCallback(async () => {
    setLoading(true)
    try {
      const response = await brandService.getAll({ search: searchTerm })
      if (response.success) {
        setMarcas(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm])

  useEffect(() => {
    loadMarcas()
  }, [loadMarcas])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        name: formData.name,
        active: formData.active
      }

      let response
      if (editingMarca) {
        response = await brandService.update(editingMarca.id, dataToSend)
      } else {
        response = await brandService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadMarcas()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar marca' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar marca:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar marca'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (marca) => {
    setEditingMarca(marca)
    setFormData({
      name: marca.name || '',
      active: marca.active !== undefined ? marca.active : true
    })
    setModalOpen(true)
  }

  const handleDelete = async (marca) => {
    if (window.confirm(`Deseja realmente excluir a marca ${marca.name}?`)) {
      try {
        const response = await brandService.delete(marca.id)
        if (response.success) {
          loadMarcas()
        }
      } catch (error) {
        console.error('Erro ao excluir marca:', error)
        alert('Erro ao excluir marca')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      active: true
    })
    setEditingMarca(null)
    setErrors({})
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    {
      key: 'active',
      label: 'Status',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="marcas-page">
      <div className="page-header">
        <div>
          <h1>
            <Tag size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Marcas
          </h1>
          <p>Gerencie marcas de produtos</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={18} />
          Nova Marca
        </button>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar marcas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={marcas}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhuma marca encontrada"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingMarca ? 'Editar Marca' : 'Nova Marca'}
        size="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingMarca ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
            placeholder="Ex: Samsung, Apple, etc"
          />

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default MarcasPage

